import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import { Grid, Paper, Container, Tooltip, Button, IconButton } from '@material-ui/core';
import { AccessDeviceActions } from 'state/modules/accessDevice';
import { OrganizationsActions } from 'state/modules/organizations';
import { uiActions } from 'state/modules/UI';
import Layout from 'components/Layout';
import Title from 'components/Title';
import { Table } from 'components/UI';
import { useStyles } from './common.styled';
import { useHistory } from 'react-router-dom';
import { SearchField } from 'components/FormFields';
import { AccessDeviceStatusSelect } from 'components/DataFields';
import { FETCH_STATE } from 'constants/common';
import { buildUrl } from 'utils';
import Can from 'components/Can';
import EditIcon from '@material-ui/icons/Edit';
import PersonIcon from '@material-ui/icons/PeopleAlt';
import { toQueryString } from 'utils';
import { debounce } from 'lodash';
import { ChooseNFCBrokerModal, CreateNFCCardModal, AssignNFCCardModal } from 'components/Modals';
import { useQuery } from 'hooks';
import DeleteIcon from '@material-ui/icons/Delete';
import { useConfirm } from 'hooks';

const ActionsMenu = ({
  row,
  setCurrentNFCID,
  setOpenCreateNFCModal,
  setOpenAssignNFCModal,
  setOpenArchiveNFCModal,
  resetAccessDevice,
}) => {
  const handleAssign = () => {
    setCurrentNFCID(row.id);
    setOpenAssignNFCModal(true);
  };

  const handleUpdate = () => {
    resetAccessDevice();
    setCurrentNFCID(row.id);
    setOpenCreateNFCModal(true);
  };

  const handleArchive = () => {
    setCurrentNFCID(row.id);
    setOpenArchiveNFCModal();
  };

  return (
    <>
      <Tooltip title={i18n.t('nfc.assign_unassign')}>
        <IconButton
          aria-label="update"
          size="medium"
          onClick={handleAssign}
          data-cy="nfc-action-assign"
        >
          <PersonIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <Tooltip title={i18n.t('common.update')}>
        <IconButton
          aria-label="update"
          size="medium"
          onClick={handleUpdate}
          data-cy="nfc-action-update"
        >
          <EditIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      {row?.status !== 'ARCHIVED' ? (
        <Tooltip title={i18n.t('common.archive')}>
          <IconButton
            aria-label="archive"
            size="medium"
            onClick={handleArchive}
            data-cy="nfc-action-archive"
          >
            <DeleteIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      ) : null}
    </>
  );
};

const NFCList = () => {
  const history = useHistory();
  const urlParams = useQuery();
  const dispatch = useDispatch();
  const classes = useStyles();
  const [params, setParams] = useState({ page: 1, limit: 10, ...urlParams });
  const abortController = useRef(null);
  const isMounted = useRef(false);
  const {
    accessDevices: data,
    pagination,
    fetchingAccessDevices,
    creatingAccessDevice,
    updatingAccessDevice,
    deletingAccessDevice,
    assigningAccessDevice,
    unassigningAccessDevice,
  } = useSelector((state) => state.accessDevice);
  const { fetchingAvailablesBrokers, availablesBrokers } = useSelector(
    (state) => state.organizations
  );
  const [openCreateNFCModal, setOpenCreateNFCModal] = useState(false);
  const [openChooseNFCBrokerModal, setOpenChooseNFCBrokerModal] = useState(false);
  const [openAssignNFCModal, setOpenAssignNFCModal] = useState(false);
  const [currentNFCID, setCurrentNFCID] = useState(null);
  const [selectedBroker, setSelectedBroker] = useState(null);

  const loading =
    fetchingAvailablesBrokers === true ||
    fetchingAccessDevices === FETCH_STATE.LOADING ||
    fetchingAccessDevices === FETCH_STATE.UNSENT ||
    deletingAccessDevice === FETCH_STATE.LOADING;

  const handleParams = (params) => {
    const data = { ...params };
    setParams(data);
    const _params = toQueryString(data);
    const url = `/NFC${_params}`;
    history.replace(buildUrl(url));
  };

  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  const handleRowClick = (row) => {
    history.push(buildUrl(`/NFC/${row?.id}`));
  };

  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  const handleSearch = (name, value) => {
    const searchParams = {
      ...params,
      page: 1,
      [name]: value,
    };
    Object.keys(searchParams).forEach(
      (key) => !searchParams[key] && searchParams[key] !== false && delete searchParams[key]
    );
    handleParams({ ...searchParams });
  };

  const handleArchiveAccessDevice = () => {
    dispatch(AccessDeviceActions.deleteAccessDevice(currentNFCID));
  };

  const handleResetAccessDevice = () => {
    dispatch(AccessDeviceActions.resetAccessDevice());
  };

  const handleBrokerSelection = (broker) => {
    setSelectedBroker(broker);
    setOpenCreateNFCModal(true);
  };

  const fetchAccessDevices = useCallback(
    (params = {}) => {
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        AccessDeviceActions.fetchAccessDevices(params, { signal: abortController?.current?.signal })
      );
    },
    [dispatch]
  );

  const fetchAccessDeviceBrokers = useCallback(() => {
    dispatch(
      OrganizationsActions.fetchAvailablesBrokers({ class_slug: 'AD', include_non_primary: true })
    );
  }, [dispatch]);

  const reload = useCallback(() => {
    dispatch(AccessDeviceActions.resetAccessDeviceState());
    setOpenCreateNFCModal(false);
    setOpenAssignNFCModal(false);
    setCurrentNFCID(null);
    fetchAccessDevices(params);
  }, [dispatch, fetchAccessDevices, params]);

  useEffect(() => {
    if (creatingAccessDevice === FETCH_STATE.DONE || updatingAccessDevice === FETCH_STATE.DONE) {
      fetchAccessDevices(params);
    }
  }, [creatingAccessDevice, fetchAccessDevices, params, updatingAccessDevice]);

  const fetchAccessDebounced = useCallback(() => {
    debounce((params) => fetchAccessDevices(params), 500);
  }, [fetchAccessDevices]);

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchAccessDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchAccessDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchAccessDevices(params);
  }, [fetchAccessDevices, params]);

  useEffect(() => {
    fetchAccessDeviceBrokers();
  }, [fetchAccessDeviceBrokers]);

  // Run on outdated data
  useEffect(() => {
    if (
      deletingAccessDevice === FETCH_STATE.DONE ||
      assigningAccessDevice === FETCH_STATE.DONE ||
      unassigningAccessDevice === FETCH_STATE.DONE
    ) {
      reload();
    }
  }, [deletingAccessDevice, assigningAccessDevice, unassigningAccessDevice, reload]);

  useEffect(() => {
    if (creatingAccessDevice === FETCH_STATE.DONE || updatingAccessDevice === FETCH_STATE.DONE) {
      setOpenCreateNFCModal(false);
      setOpenAssignNFCModal(true);
    }
  }, [creatingAccessDevice, updatingAccessDevice]);

  const columns = [
    { name: i18n.t('common.id'), selector: 'id', sortable: true, grow: 0 },
    {
      name: i18n.t('nfc.serial_number'),
      selector: 'device_id',
      sortable: true,
      grow: 1,
    },
    {
      name: i18n.t('common.user'),
      selector: 'user.last_name',
      cell: (row) => (row?.user?.id ? `${row?.user?.last_name}, ${row?.user?.first_name}` : '--'),
      grow: 1,
    },
    {
      name: i18n.t('nfc.user_id'),
      selector: 'user.id',
      cell: (row) => row?.user?.id || '--',
      grow: 1,
    },
    {
      name: i18n.t('nfc.card_name'),
      selector: 'name',
      sortable: true,
      grow: 1,
    },
    {
      name: i18n.t('common.status'),
      selector: 'status',
      cell: (row) => i18n.t(`nfc.status.${row?.status}`, { defaultValue: row?.status || '--' }),
      grow: 1,
    },
    {
      name: i18n.t('common.actions'),
      cell: (row) => (
        <ActionsMenu
          row={row}
          setCurrentNFCID={setCurrentNFCID}
          setOpenCreateNFCModal={setOpenCreateNFCModal}
          setOpenAssignNFCModal={setOpenAssignNFCModal}
          setOpenArchiveNFCModal={toggleArchive}
          resetAccessDevice={handleResetAccessDevice}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      sortable: false,
      center: true,
      grow: 1,
    },
  ];

  const [toggleArchive, ArchiveConfirm] = useConfirm({
    message: i18n.t('nfc.archive_description_nfc_card'),
    title: i18n.t('nfc.archive_nfc_card'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.confirm'),
    onConfirm: handleArchiveAccessDevice,
    additionalProps: { modalProps: { maxWidth: 700 } },
  });

  return (
    <Layout title={i18n.t('nfc.title')}>
      <ArchiveConfirm />
      <ChooseNFCBrokerModal
        availablesBrokers={availablesBrokers}
        broker={selectedBroker}
        handleBrokerSelection={handleBrokerSelection}
        open={openChooseNFCBrokerModal}
        handleClick={() => {
          setOpenChooseNFCBrokerModal(false);
        }}
      />
      <CreateNFCCardModal
        id={currentNFCID}
        broker={selectedBroker}
        open={openCreateNFCModal}
        handleClick={() => {
          setOpenCreateNFCModal(false);
          setCurrentNFCID(null);
          if (
            creatingAccessDevice === FETCH_STATE.DONE ||
            updatingAccessDevice === FETCH_STATE.DONE
          ) {
            reload();
          }
          if (!currentNFCID && availablesBrokers.length > 1) {
            setOpenChooseNFCBrokerModal(true);
          }
        }}
      />
      <AssignNFCCardModal
        id={currentNFCID}
        open={openAssignNFCModal}
        handleClick={() => {
          setOpenAssignNFCModal(false);
          setCurrentNFCID(null);
          if (assigningAccessDevice === FETCH_STATE.DONE) {
            reload();
          }
        }}
      />
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item>
                <Title title={i18n.t('nfc.title')} />
              </Grid>
              <Can I="read" a="access_device">
                <Grid item>
                  <Button
                    style={{ textTransform: 'none' }}
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      dispatch(AccessDeviceActions.resetAccessDevice());
                      setCurrentNFCID(null);
                      if (availablesBrokers.length === 1) {
                        setSelectedBroker(availablesBrokers[0]);
                        setOpenCreateNFCModal(true);
                      } else if (availablesBrokers.length > 1) {
                        setSelectedBroker(null);
                        setOpenChooseNFCBrokerModal(true);
                      } else {
                        dispatch(
                          uiActions.handleShowSnackbar({
                            type: 'error',
                            message: i18n.t('nfc.no_broker_available'),
                          })
                        );
                      }
                    }}
                    data-cy="create-nfc-button"
                  >
                    {i18n.t('nfc.create')}
                  </Button>
                </Grid>
              </Can>
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ marginBottom: '25px' }}>
            <Grid
              container
              spacing={2}
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-end"
            >
              <Grid item style={{ width: '25rem' }}>
                <SearchField
                  label={i18n.t('nfc.search_by')}
                  field={{
                    onChange: handleSearch,
                    name: 'keyword',
                    value: params.keyword || '',
                  }}
                  margin="none"
                  data-cy="filter-nfc-keyword"
                />
              </Grid>
              <Grid item style={{ width: '15rem' }}>
                <AccessDeviceStatusSelect
                  field={{
                    name: 'status',
                    value: params.status !== 'ARCHIVED' ? params.status : '',
                  }}
                  margin="none"
                  form={{ setFieldValue: handleSearch }}
                  label={i18n.t('common.by_status')}
                  data-cy="filter-nfc-status"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                data={data}
                columns={columns}
                onSort={handleSort}
                loading={loading}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
                page={params.page}
                limit={params.limit}
                data-cy="nfc-table"
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

ActionsMenu.propTypes = {
  row: PropTypes.object,
  setCurrentNFCID: PropTypes.func,
  setOpenCreateNFCModal: PropTypes.func,
  setOpenAssignNFCModal: PropTypes.func,
  setOpenArchiveNFCModal: PropTypes.func,
  resetAccessDevice: PropTypes.func,
};

export { NFCList };
